import { useTranslation } from 'react-i18next';
import useU2000AxiosAuthenticated from '../../u2000Core/axios/U2000UseAxiosAuthenticated';

import U2000BasicTable, { type U2000SearchPayload, type U2000SearchResult } from '../../u2000Core/components/tables/basic/U2000BasicTable';
import { U2000IdColumnDef, type U2000TableColumn } from '../../u2000Core/components/tables/basic/U2000BasicTableList';
import React, { useEffect, useRef } from 'react';
import { useToast } from '../../u2000Core/context/toast/U2000ToastContext';
import { U2000HandleApiError } from '../../u2000Core/axios/U2000HandleApiError';
import { useU2000SearchHandlers } from '../../u2000Core/hooks/handler/U2000UseSearchHandlers';
import { useU2000QuickSearchListEffect } from '../../u2000Core/hooks/useU2000QuickSearchListEffect';
import { useNavigate } from 'react-router-dom';
import type { TFunction } from 'i18next';

import type { U2000TableHandlers } from '../../u2000Core/types/U2000TableHandlers';
import { U2000GridStateEnumPaginationDirection } from '../../u2000Core/reducer/formReducer/U2000GridState';
import { U2000LUStateEnumNextAction } from '../../u2000Core/reducer/formReducer/U2000LUState';
import { useU2000DeleteHandlers } from '../../u2000Core/hooks/handler/U2000UseDeleteHandlers';
import { U2000GridStateDeleteAction, type U2000GridStateNextPageAfterDeleteReturn } from '../../u2000Core/reducer/formReducer/U2000GridStateHandlers';
import { useU3203Context } from '../U3203Context';
import { doDeleteData, doSearch, luHandleAppiError, type U3203AiMissionSearchRow } from '../U3203Service';
import { NS } from '../U3203';
import U3203AiMissionDeleteDlg from './common/U3203AiMissionDeleteDlg';
import { U3203AiMissionListToolBar } from './list/U3203AiMissionListToolBar';
import { useU2000LogicalUnitContext } from '../../u2000Core/context/logicalUnit/U2000LogicalUnitContext';
import { u2000BuildQuickSearchFormPath, u2000ShouldQuickSearchOpenForm } from '../../u2000Core/tools/U2000QuickSearchUtils';
import { useU2000AuthContext } from '../../u2000Core/context/auth/U2000UseAuthContext';
import { u2000CanManageScopeAwareEntity, u2000CanModifyU3203Mission } from '../../u2000Core/tools/U2000SystemScopeAccess';
import { u3203CategoryLabel } from '../utils/u3203CategoryLabels';
import { useU3203CorpusHandlers } from '../hooks/useU3203CorpusHandlers';

function parseBooleanGridValue(value: unknown): boolean | null {
  if (value === true || value === 'true' || value === 1 || value === '1') return true;
  if (value === false || value === 'false' || value === 0 || value === '0') return false;
  if (typeof value === 'object' && value !== null) {
    const o = value as Record<string, unknown>;
    if (typeof o.value === 'boolean') return o.value;
    if (o.value === 'true' || o.value === 1) return true;
    if (o.value === 'false' || o.value === 0) return false;
  }
  return null;
}

function getColumnsDef(t: TFunction, corpusLabelById: Record<string, string>): U2000TableColumn[] {
  return [
    U2000IdColumnDef,
    {
      name: 'code',
      label: t('lblCode', { ns: NS }),
      widthClass: 'w-[180px]',
      type: 'string',
      align: 'left',
      isCriteriaField: true,
      inGridField: true,
      isDefaultColumn: true,
      sortable: true,
      isDefaultSortedColumn: true,
    },
    {
      name: 'title',
      label: t('lblTitle', { ns: NS }),
      widthClass: 'w-[240px]',
      type: 'string',
      align: 'left',
      isCriteriaField: true,
      inGridField: true,
      isDefaultColumn: true,
      sortable: true,
      isDefaultSortedColumn: false,
    },
    {
      name: 'category',
      label: t('lblCategory', { ns: NS }),
      widthClass: 'w-[140px]',
      type: 'string',
      align: 'left',
      isCriteriaField: true,
      inGridField: true,
      isDefaultColumn: true,
      sortable: true,
      isDefaultSortedColumn: false,
      includeConditions: ['equals', 'notequals'],
      display: (_column, value) => u3203CategoryLabel(t, String(value ?? '')),
    },
    {
      name: 'linkedLogicalUnit',
      label: t('lblLinkedLogicalUnit', { ns: NS }),
      widthClass: 'w-[120px]',
      type: 'string',
      align: 'left',
      isCriteriaField: true,
      inGridField: true,
      isDefaultColumn: true,
      sortable: true,
      isDefaultSortedColumn: false,
    },
    {
      name: 'corpusId',
      label: t('lblCorpusRag', { ns: NS }),
      widthClass: 'w-[200px]',
      type: 'string',
      align: 'left',
      isCriteriaField: false,
      inGridField: true,
      isDefaultColumn: true,
      sortable: false,
      isDefaultSortedColumn: false,
      display: (_column, value) => {
        if (value == null || value === '') return null;
        const id = String(value);
        return corpusLabelById[id] ?? null;
      },
    },
    {
      name: 'active',
      label: t('lblActive', { ns: NS }),
      widthClass: 'w-[100px]',
      type: 'boolean',
      align: 'center',
      isCriteriaField: true,
      inGridField: true,
      isDefaultColumn: true,
      sortable: true,
      isDefaultSortedColumn: false,
      includeConditions: ['equals', 'notequals'],
      display: (_column, value) => {
        const b = parseBooleanGridValue(value);
        if (b === true) return t('lblActiveYes', { ns: NS });
        if (b === false) return t('lblActiveNo', { ns: NS });
        return null;
      },
    },
    {
      name: 'systemScope',
      label: t('lblSystemScope', { ns: NS }),
      widthClass: 'w-[120px]',
      type: 'boolean',
      align: 'center',
      isCriteriaField: true,
      inGridField: true,
      isDefaultColumn: true,
      sortable: true,
      isDefaultSortedColumn: false,
      includeConditions: ['equals', 'notequals'],
      display: (_column, value) => {
        const b = parseBooleanGridValue(value);
        if (b === true) {
          return (
            <span className="rounded-sm bg-slate-200 px-2 py-0.5 text-xs font-medium text-slate-800">
              {t('valSystemScope', { ns: NS })}
            </span>
          );
        }
        return null;
      },
    },
    {
      name: 'revisionNumber',
      label: t('lblRevisionNumber', { ns: NS }),
      widthClass: 'w-[100px]',
      type: 'number',
      align: 'center',
      isCriteriaField: false,
      inGridField: true,
      isDefaultColumn: true,
      sortable: true,
      isDefaultSortedColumn: false,
    },
  ];
}

const U3203AiMissionList: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const toast = useToast();
  const { auth, hasRole } = useU2000AuthContext();

  const { luStateHandlers, searchStateHandlers, gridStateHandlers } = useU3203Context();
  const u2000AxiosAuthenticated = useU2000AxiosAuthenticated();
  const { setLogicalUnitBreadcrumb, quickSearchData, setQuickSearchData } = useU2000LogicalUnitContext();
  const consumeQsSessionRef = useRef<() => { isFromQuickSearch: boolean; targetLogicalUnit: string } | null>(() => null);
  const { corpusLabelById, loadCorpora } = useU3203CorpusHandlers();
  const canModifyRow = (row: U3203AiMissionSearchRow) =>
    u2000CanModifyU3203Mission({
      isSystemScope: row.systemScope === true,
      hasEditRole: hasRole('U3203:EDIT'),
      sessionOrganizationId: auth.user.organizationId,
    });
  const canDeleteRow = (row: U3203AiMissionSearchRow) =>
    u2000CanManageScopeAwareEntity({
      isSystemScope: row.systemScope === true,
      hasRequiredRole: hasRole('U3203:DELETE'),
      sessionOrganizationId: auth.user.organizationId,
    });

  useEffect(() => {
    void loadCorpora();
  }, [loadCorpora]);

  useEffect(() => {
    const listTitle = t('title', { ns: NS });
    searchStateHandlers.setColumns(getColumnsDef(t, corpusLabelById));
    gridStateHandlers.setColumns(getColumnsDef(t, corpusLabelById));
    setLogicalUnitBreadcrumb([{ label: listTitle }]);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- handlers instables
  }, [t, corpusLabelById]);

  const handleSearch = useU2000SearchHandlers<U3203AiMissionSearchRow>({
    doSearchFn: async (searchPayload: U2000SearchPayload) => {
      return await doSearch(u2000AxiosAuthenticated, searchPayload);
    },
    onSuccess: (searchResult: U2000SearchResult<U3203AiMissionSearchRow>, direction: U2000GridStateEnumPaginationDirection) => {
      gridStateHandlers.initGridAfterSearch(searchResult, direction);
      searchStateHandlers.keepFilterAfterSearch(searchStateHandlers.extractSearchParameter());

      const qsSession = consumeQsSessionRef.current();
      const isFromQuickSearch = qsSession?.isFromQuickSearch === true;
      const targetLU = qsSession?.targetLogicalUnit ?? '/U3203';
      if (
        u2000ShouldQuickSearchOpenForm(window.location.pathname, targetLU) &&
        searchResult.data &&
        searchResult.data.length === 1 &&
        isFromQuickSearch
      ) {
        const single = searchResult.data[0];
        luStateHandlers.setNextAction(U2000LUStateEnumNextAction.NONE);
        luStateHandlers.setActionOriginFrom('');
        const currentIdx = gridStateHandlers.getData().findIndex((r) => r.id === single.id);
        if (currentIdx !== -1) {
          gridStateHandlers.setCurrentIdx(currentIdx);
        }
        navigate(u2000BuildQuickSearchFormPath(targetLU, single.id), { replace: true });
      }
    },
    onError: ({ error }: { error: unknown }) => {
      const errorResult = U2000HandleApiError(error as any, t, NS, luHandleAppiError);
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      }
    },
  });

  const handleDelete = useU2000DeleteHandlers({
    getSelectedRows: () => (gridStateHandlers ? gridStateHandlers.getSelectedRows() : []),
    doDeleteFn: async (id, row) => {
      return await doDeleteData(u2000AxiosAuthenticated, id, { id: row.id, version: row.version });
    },

    onSuccess: (deletedRows) => {
      const page = gridStateHandlers.getCurrentPage();
      let newData = gridStateHandlers.getData();
      let totalRows = gridStateHandlers.getTotalRows();

      deletedRows.forEach((row) => {
        toast.success(t('success0003', { key: row.code ?? row.id, ns: NS }), t('successTitle', { ns: NS }));
        newData = newData.filter((dataRow) => dataRow.id !== row?.id);
        totalRows -= 1;
      });
      gridStateHandlers.setData(newData);
      gridStateHandlers.setTotalRows(totalRows);

      const action: U2000GridStateNextPageAfterDeleteReturn = gridStateHandlers.getNextPageAfterDelete(page, totalRows);
      if (action.action === U2000GridStateDeleteAction.PAGENEXT && action.page) {
        handleSearch.handleSearch(searchStateHandlers.extractSearchParameter(), gridStateHandlers.extractPagination(action.page), U2000GridStateEnumPaginationDirection.NEXTPAGE);
      }
    },

    onError: (errors) => {
      errors.forEach(({ error }) => {
        const errorResult = U2000HandleApiError(error, t, NS, luHandleAppiError);
        if (errorResult.genericErrors?.length) {
          errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
        }
      });
    },
  });

  const u3203ListHandlerActions: U2000TableHandlers<U3203AiMissionSearchRow> = {
    onSearch: (page: number, direction: U2000GridStateEnumPaginationDirection) => {
      handleSearch.handleSearch(searchStateHandlers.extractSearchParameter(), gridStateHandlers.extractPagination(page), direction);
    },
    onRowClick: (row: U3203AiMissionSearchRow) => {
      luStateHandlers.setNextAction(U2000LUStateEnumNextAction.NONE);
      luStateHandlers.setActionOriginFrom('');
      const currentIdx = gridStateHandlers.getData().findIndex((r) => r.id === row.id);
      gridStateHandlers.setCurrentIdx(currentIdx);
      navigate(`${row.id}`);
    },
    onAdd: () => {
      luStateHandlers.setNextAction(U2000LUStateEnumNextAction.ADD);
      luStateHandlers.setActionOriginFrom('/U3203');
      navigate('new');
    },
    onModify: () => {
      const row = gridStateHandlers.getSelectedRows()[0];
      if (!row || !canModifyRow(row)) return;
      luStateHandlers.setNextAction(U2000LUStateEnumNextAction.MODIFY);
      luStateHandlers.setActionOriginFrom('/U3203');
      navigate(`${row?.id}`);
    },
    onDelete: () => {
      const rows = gridStateHandlers.getSelectedRows();
      if (rows.some((row) => !canDeleteRow(row))) return;
      handleDelete.handleDelete();
    },
  };

  const { consumeQuickSearchSession } = useU2000QuickSearchListEffect({
    targetLogicalUnit: '/U3203',
    t,
    searchStateHandlers,
    gridStateHandlers,
    runSearch: handleSearch.handleSearch,
    quickSearchData,
    setQuickSearchData,
  });
  consumeQsSessionRef.current = consumeQuickSearchSession;

  if (searchStateHandlers.getColumns().length === 0) {
    return null;
  }

  return (
    <>
      <U3203AiMissionDeleteDlg handleDelete={handleDelete} t={t} NS={NS} />
      <U2000BasicTable
        searchStateHandlers={searchStateHandlers}
        gridStateHandlers={gridStateHandlers}
        actionsButtons={
          <U3203AiMissionListToolBar gridStateHandlers={gridStateHandlers} handlerActions={u3203ListHandlerActions} />
        }
        handlerActions={u3203ListHandlerActions}
        label={t('lblMissionList', {
          count: gridStateHandlers.getTotalRows(),
          ns: NS,
        })}
        allowRowSelection={true}
        hasExportRole={false}
      />
    </>
  );
};

export default U3203AiMissionList;
